import { Button, Form, Input, Select, Switch } from "antd";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { jcTypeArray } from "../../utils";
import { authenticateToken } from "../../utils/auth";
import { responseNotification } from "../../utils/notify";
import ImageUploader from "../FileManager/upload";
import axios from "axios";
import MapBox from "../common/MapBox";

const { Option } = Select;

const AddHub = ({ visibleData, onCloseMethod }: any) => {
  const [generatedImgUrl, setGeneratedImgUrl] = useState<string>();
  const [startUpload, setStartUpload] = useState<
    "Initiated" | "Uploading" | "Uploaded"
  >(undefined);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hubData, setHubData] = useState(undefined);
  const [hubUrl, setHubUrl] = useState(undefined);
  const [error, setError] = useState("");
  const [freeDelivery, setFreeDelivery] = useState(undefined);
  const [key, setKey] = useState<string>();

  const [locationOptions, setLocationOptions] = useState<any>({
    list: [],
    loading: false,
  });

  const [currentLocation, setCurrentLocation] = useState({
    lat: 23.77340239600077,
    lng: 90.41329051290532,
  });

  // ******************************
  const getLocationOptions = useCallback(async (key?: string) => {
    setLocationOptions({ loading: true, list: [] });
    const encodedUri = `${process.env.REACT_APP_MAP_API}`;
    const res = await axios.get(
      `${encodedUri}/autocomplete` + (key ? `?key=${key}` : ``),
      {
        headers: {
          Authorization: `Bearer ${authenticateToken()}`,
        },
      }
    );

    setLocationOptions({
      loading: false,
      list: res?.data?.places,
    });
  }, []);

  // ******************************

  const onSubmit = useCallback(
    async (data) => {
      if (generatedImgUrl) {
        setLoading(true);

        const readyData = data && {
          name: data.name,
          type: data.type,
          baseDeliveryCharge: parseInt(data.baseDeliveryCharge) || 0,
          isFreeDelivery: data.isFreeDelivery || false,
          minimumAmountForFreeDelivery: freeDelivery
            ? null
            : parseInt(data.minimumAmountForFreeDelivery),
          image: generatedImgUrl ? generatedImgUrl : data?.image,
          location: {
            latitude: data.latitude,
            longitude: data.longitude,
          },
        };

        if (hubData) {
          await fetch(`${process.env.REACT_APP_CATALOG_WRITER_API}/hub`, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${authenticateToken()}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...readyData,
              id: hubData?.id,
            }),
          })
            .then((res) => res.json())
            .then((res) => {
              setLoading(false);

              if (res.statusCode === 200) {
                responseNotification("Hub Updated Successfully", "success");
                form.resetFields();
                if (onCloseMethod) {
                  onCloseMethod();
                }
              } else if (res.status === 500) {
                responseNotification("Internal server error", "error");
              } else {
                responseNotification(
                  res.message || "something wrong",
                  "warning"
                );
              }
            })
            .catch((err) => {
              setLoading(false);
              responseNotification(
                `${"Internal server error"} ${err}`,
                "error"
              );
              console.error("err", err);
            });
        } else {
          await fetch(`${process.env.REACT_APP_CATALOG_WRITER_API}/hub`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authenticateToken()}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(readyData),
          })
            .then((res) => res.json())
            .then((res) => {
              setLoading(false);

              if (res.statusCode === 200) {
                responseNotification("Hub Create Successfully", "success");
                form.resetFields();
                if (onCloseMethod) {
                  onCloseMethod();
                }
              } else if (res.status === 500) {
                responseNotification("Internal server error", "error");
              } else {
                responseNotification(
                  res.message || "something wrong",
                  "warning"
                );
              }
            })
            .catch((err) => {
              setLoading(false);
              responseNotification(
                `${"Internal server error"} ${err}`,
                "error"
              );
              console.error("err", err);
            });
        }
      } else {
        responseNotification(`Image required`, "error");
      }
    },
    [generatedImgUrl]
  );

  useEffect(() => {
    if (visibleData) {
      setHubData(visibleData);
      setGeneratedImgUrl(visibleData?.image);
    }
  }, [visibleData]);

  useEffect(() => {
    if (hubData?.isFreeDelivery) {
      setFreeDelivery(hubData?.isFreeDelivery || false);
    }
  }, [hubData?.isFreeDelivery]);

  useEffect(() => {
    if (key) {
      getLocationOptions();
    }
  }, [getLocationOptions, key]);

  useEffect(() => {
    if (hubData) {
      form.resetFields(Object.keys(visibleData));
      setHubUrl(visibleData?.image);
      setCurrentLocation({
        lat: hubData?.location.y,
        lng: hubData?.location.x,
      });
    }
  }, [form, hubData, visibleData]);

  useEffect(() => {
    if (startUpload === "Uploaded") {
      form.submit();
    }
  }, [form, startUpload]);

  const fetchRef = useRef(0);
  const handleSearch = React.useMemo(() => {
    const loadOptions = (value: string, field: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;

      if (fetchId !== fetchRef.current) {
        return;
      }

      if (value) {
        if (field === "locations") getLocationOptions(value);
      }
    };

    return debounce(loadOptions, 1200);
  }, [getLocationOptions]);

  useEffect(() => {
    form.resetFields(["latitude", "longitude"]);
  }, [currentLocation]);

  return (
    <div className="add-product drawer-toggle-wrapper">
      <div className="drawer-toggle-inner-wrapper">
        <Form
          name="control-hooks"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          className="ant-form ant-form-vertical"
          onFinish={onSubmit}
          form={form} // like ref
          layout="vertical"
          initialValues={{
            ...hubData,
          }}
        >
          <Form.Item
            hasFeedback
            label="Type"
            rules={[
              {
                required: true,
                message: "Type is required!",
              },
            ]}
            name="type"
          >
            <Select placeholder="Hub Type" style={{ minWidth: 300 }}>
              {Object.values(jcTypeArray)?.map((type, i) => (
                <Select.Option value={type} key={i}>
                  {type?.replace("_", " ")}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            hasFeedback
            label="Hub Name"
            rules={[
              {
                required: true,
                message: "Hub Name is Required!",
              },
            ]}
            name="name"
          >
            <Input id="name" type="text" placeholder="Enter Hub Name" />
          </Form.Item>

          <Form.Item
            hasFeedback
            label="Base Delivery Charge"
            rules={[
              {
                required: true,
                message: "Delivery Charge is Required!",
              },
              {
                validator: async (_, name) => {
                  if (name && !(parseFloat(name) > 0)) {
                    return Promise.reject(new Error("Invalid Delivery Charge"));
                  }
                },
              },
            ]}
            name="baseDeliveryCharge"
          >
            <Input
              id="baseDeliveryCharge"
              type="text"
              placeholder="Enter Delivery Charge"
            />
          </Form.Item>

          <Form.Item
            hasFeedback
            label="Free Delivery?"
            name="isFreeDelivery"
            style={{ minWidth: "100%" }}
          >
            <Switch
              checkedChildren="Yes"
              unCheckedChildren="No"
              defaultChecked={hubData?.isFreeDelivery}
              onChange={setFreeDelivery}
            />
          </Form.Item>

          {!freeDelivery && (
            <>
              <Form.Item
                hasFeedback
                label="Minimum Amount For Free Delivery"
                name="minimumAmountForFreeDelivery"
                rules={[
                  { required: true, message: "Minimum Amount is Required" },
                ]}
              >
                <Input
                  id="minimumAmountForFreeDelivery"
                  type="text"
                  placeholder="Enter Minimum Amount"
                />
              </Form.Item>
            </>
          )}

          <Form.Item hasFeedback label="Address" style={{ minWidth: "98%" }}>
            <Select
              allowClear
              autoClearSearchValue={false}
              showSearch
              placeholder="Select Address"
              optionFilterProp="children"
              onSearch={(val) => {
                handleSearch?.(val, "locations");
              }}
              onSelect={(val) => {
                const matchData = locationOptions?.list?.find(
                  (place) => val === place.id
                );

                setCurrentLocation({
                  lat: matchData?.location?.latitude,
                  lng: matchData?.location?.longitude,
                });
              }}
              filterOption={(input, option) =>
                option.children
                  .toString()
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {locationOptions?.list?.map((place) => (
                <Option value={place?.id} key={place?.id}>
                  {place?.address}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            hasFeedback
            label="Latitude"
            name="latitude"
            initialValue={currentLocation?.lat}
          >
            <Input id="latitude" type="text" placeholder="23.86450" />
          </Form.Item>

          <Form.Item
            hasFeedback
            label="Longitude"
            name="longitude"
            initialValue={currentLocation?.lng}
          >
            <Input id="longitude" type="text" placeholder="90.53680" />
          </Form.Item>

          <MapBox
            draggable={true}
            coordinate={currentLocation}
            setCoordinator={(val) => setCurrentLocation(val)}
          />

          <h4 className="title" style={{ maxWidth: "99%" }}>
            Image:
          </h4>
          <div className="">
            <ImageUploader
              imgUrl={generatedImgUrl || ""}
              startUpload={startUpload}
              setStartUpload={(val: any) => setStartUpload(val)}
              setGeneratedImgUrl={(url: any) => setGeneratedImgUrl(url)}
            />
          </div>

          <div className="buttons-container">
            <Button
              disabled={loading || !!error}
              loading={(loading ? "loading" : undefined) as any}
              type="primary"
              htmlType="submit"
              className="add-submit-btn text-center mr-2"
              onClick={(e) => {
                e.preventDefault();
                setStartUpload("Uploading");
              }}
            >
              Submit
            </Button>
            <Button
              type="ghost"
              htmlType="button"
              onClick={() => {
                form?.resetFields();
                setHubUrl(undefined);
                setGeneratedImgUrl(undefined);
                setStartUpload(undefined);
              }}
              className="reset-submit-btn text-center mr-2"
            >
              Reset
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddHub;
